export class SoundManager {
  private enabled: boolean = false;
  private lastSoundTime: number = 0;
  private soundCooldown: number = 100; // 100ms cooldown between sounds
  private audioContext: AudioContext | null = null;
  private masterVolume: number = 0.3;

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor() {
    // Audio disabled by default to prevent crashes
  }

  private initAudioContext() {
    // Disabled to prevent crashes
    return;
  }

  private canPlaySound(): boolean {
    const now = Date.now();
    if (now - this.lastSoundTime < this.soundCooldown) {
      return false;
    }
    this.lastSoundTime = now;
    return this.enabled && !!this.audioContext;
  }

  private createTone(frequency: number, duration: number = 0.2, volume: number = 0.3) {
    if (!this.canPlaySound() || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume * this.masterVolume, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (error) {
      console.warn('Audio playback failed:', error);
    }
  }

  private playMultiTone(frequencies: number[], intervals: number[] = []) {
    if (!this.canPlaySound()) return;

    frequencies.forEach((freq, index) => {
      const delay = intervals[index] || 0;
      setTimeout(() => this.createTone(freq, 0.15, 0.25), delay);
    });
  }

  playUploadSound() {
    this.createTone(523, 0.3, 0.4); // C5 - pleasant upload confirmation
  }

  playMatchSound() {
    // Happy ascending chord: C5 -> E5 -> G5
    this.playMultiTone([523, 659, 784], [0, 80, 160]);
  }

  playMismatchSound() {
    // Dissonant descending tone: F4 -> C4
    this.playMultiTone([349, 262], [0, 120]);
  }

  playDragSound() {
    // Subtle low tone to avoid spam
    this.createTone(220, 0.1, 0.15); // A3
  }

  playDropSound() {
    this.createTone(392, 0.15, 0.25); // G4 - confirmation tone
  }

  playStreakSound(streak: number) {
    // Higher pitch for longer streaks
    const basePitch = 523; // C5
    const pitch = basePitch + (streak * 50);
    this.createTone(Math.min(pitch, 1046), 0.25, 0.35); // Cap at C6
  }

  playLevelCompleteSound() {
    // Victory fanfare: C5 -> E5 -> G5 -> C6
    this.playMultiTone([523, 659, 784, 1046], [0, 100, 200, 300]);
  }

  playHoverSound() {
    // Very subtle high pitch
    this.createTone(880, 0.05, 0.1); // A5
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  isEnabled() {
    return this.enabled;
  }
}

export class HapticManager {
  private enabled: boolean = false;
  private lastVibrationTime: number = 0;
  private vibrationCooldown: number = 300; // 300ms cooldown

  private canVibrate(): boolean {
    const now = Date.now();
    if (now - this.lastVibrationTime < this.vibrationCooldown) {
      return false;
    }
    this.lastVibrationTime = now;
    return this.enabled;
  }

  private vibrate(pattern: number | number[]) {
    if (!this.canVibrate()) return;
    
    try {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    } catch (error) {
      // Silent fail to prevent crashes
    }
  }

  lightTap() {
    this.vibrate(20);
  }

  mediumTap() {
    this.vibrate(50);
  }

  heavyTap() {
    this.vibrate(100);
  }

  success() {
    this.vibrate(50);
  }

  error() {
    this.vibrate(100);
  }

  levelComplete() {
    this.vibrate(200);
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  isEnabled() {
    return this.enabled;
  }
}